import { useCallback, useState } from "react";
import { Button, Card, IconButton, Input, Popover, Stack } from "@introist/react-foundation/v2";
import { Link2 } from "lucide-react";
import { Editor } from "@tiptap/react";
import styled from "styled-components";

const StyledInput = styled(Input)`
  && {
    min-width: 320px;
  }
`;

export const LinkSelector = ({ editor }: { editor?: Editor | null }) => {
  const [popoverRef, setPopoverRef] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const [link, setLink] = useState<string>();
  const [linkText, setLinkText] = useState<string>();

  const onSubmit = useCallback(
    (link?: string, text?: string) => {
      if (!editor) return;
      if (!link) {
        editor.chain().focus().extendMarkRange("link").unsetLink().run();
        return;
      }

      // If link text is provided, replace the entire link with new text and URL
      if (text !== undefined && text !== "") {
        editor
          .chain()
          .focus()
          .extendMarkRange("link")
          .deleteSelection()
          .insertContent(`<a href="${link}">${text}</a>`)
          .run();
      } else {
        // Just update the link URL
        editor.chain().focus().extendMarkRange("link").setLink({ href: link }).run();
      }
    },
    [editor]
  );

  const handleSubmit = () => {
    onSubmit(link, linkText);
    setOpen(false);
    setLink(undefined);
    setLinkText(undefined);
  };

  return (
    <>
      <Stack ref={setPopoverRef}>
        <IconButton
          icon={<Link2 size={16} />}
          onClick={() => {
            setOpen(!open);
            const href = editor?.getAttributes("link").href ?? undefined;
            setLink(href);

            // Extract the link text from the editor
            if (editor) {
              if (editor.isActive("link")) {
                // Temporarily extend selection to cover the full link to extract its text
                const { state } = editor;
                const { from, to } = state.selection;

                // Store the original selection
                const originalFrom = from;
                const originalTo = to;

                // Extend the selection to cover the entire link mark
                editor.chain().extendMarkRange("link").run();

                // Get the extended selection
                const { from: linkStart, to: linkEnd } = editor.state.selection;
                const text = editor.state.doc.textBetween(linkStart, linkEnd, "");
                setLinkText(text || undefined);

                // Restore original selection
                editor.commands.setTextSelection({ from: originalFrom, to: originalTo });
              } else {
                const { from, to } = editor.state.selection;
                const text = editor.state.doc.textBetween(from, to, "");
                setLinkText(text || undefined);
              }
            }
          }}
          size="large"
          active={!!editor?.isActive("link")}
        />
      </Stack>
      <Popover
        referenceElement={popoverRef}
        open={open}
        onClose={() => {
          setOpen(false);
          setLink(undefined);
          setLinkText(undefined);
        }}
        closeOnContentClick={false}
      >
        <Card elevated style={{ padding: "var(--spacing-small)" }}>
          <Stack vertical gap="xSmall">
            <StyledInput
              value={linkText}
              onChange={setLinkText}
              placeholder="Link text"
              size="small"
              autoFocus
              onKeyUp={e => {
                if (e.key === "Enter") {
                  handleSubmit();
                }
              }}
            />
            <StyledInput
              value={link}
              onChange={setLink}
              placeholder="Enter link URL"
              size="small"
              onKeyUp={e => {
                if (e.key === "Enter") {
                  handleSubmit();
                }
              }}
            />

            <Button size="small" variant="blended" onClick={handleSubmit}>
              Save
            </Button>
          </Stack>
        </Card>
      </Popover>
    </>
  );
};
